import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { InfoCircleTwoTone } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import HoverHelp from '../common/HoverHelp';
import { getIdMap } from '../helpers/helpers';
import { getAllDayValues } from '../helpers/time';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';

const compactTime = (dt) => {
  if (!dt?.isValid) return '';
  return dt.minute ? dt.toFormat('h:mm a') : dt.toFormat('h a');
};

const timeLabel = (startTime, endTime, timezone) => {
  if (!startTime || !endTime) return '';
  const zone = timezone || DateTime.local().zoneName;
  const startDT = DateTime.fromMillis(startTime, { zone });
  const endDT = DateTime.fromMillis(endTime, { zone });
  if (!startDT.isValid || !endDT.isValid) return '';
  const zoneSuffix = zone !== DateTime.local().zoneName ? ` ${startDT.toFormat('ZZZZ')}` : '';
  const { isAllDay } = getAllDayValues(startDT, endDT);
  if (isAllDay && startDT.hasSame(endDT, 'day')) return `All day${zoneSuffix}`;
  const startText = compactTime(startDT);
  const endText = compactTime(endDT);
  if (startText && endText) return `${startText} – ${endText}${zoneSuffix}`;
  return `${startText || endText}${zoneSuffix}`;
};

export default function ScheduleDrawerHeader({
  isNew,
  isDraft,
  isEvent,
  title,
  color,
  projectId,
  startTime,
  endTime,
  timezone,
  showGantt,
  ganttTaskName,
  onGanttClick,
}) {
  const projects = useSelector((state) => state.projects.projects);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const project = projectMap[projectId];
  const projectName = project
    ? formatProjectLabelFromCompanySettings({
      name: project.name,
      number: project.number,
      settings,
    })
    : '';

  const noun = isEvent ? 'event' : 'shift';
  let heading;
  if (isNew) {
    heading = `Add ${noun}`;
  } else if (title) {
    heading = title;
  } else if (isEvent) {
    heading = 'Event';
  } else {
    heading = 'Shift';
  }

  let detail;
  if (isNew) {
    detail = `Fill in the ${noun} details, then create`;
    if (!isEvent) {
      detail += ' or save as draft';
    }
    detail += '.';
  } else {
    detail = [projectName, timeLabel(startTime, endTime, timezone)].filter(Boolean).join(' · ');
  }

  const hover = (
    <HoverHelp
      content={`This shift was dispatched from a gantt task ${ganttTaskName ?? ''}.`}
      Icon={InfoCircleTwoTone}
      iconProps={{ twoToneColor: '#fdb81b', style: { height: 20, fontSize: '18px' } }}
    />
  );

  return (
    <div className="schedule-drawer-header">
      <div className="schedule-drawer-header-body">
        <div className="schedule-drawer-header-title-row">
          <span
            className="schedule-drawer-header-accent"
            style={{ backgroundColor: color || '#606775' }}
          />
          {showGantt && (
            onGanttClick ? (
              <Button
                type="link"
                onClick={onGanttClick}
                style={{ marginTop: -3, padding: 0, height: 'auto' }}
              >
                {hover}
              </Button>
            ) : <div>{hover}</div>
          )}
          <span className="schedule-drawer-header-title">{heading}</span>
          {!isNew && (
            <span className={`schedule-drawer-header-status ${isDraft ? 'is-draft' : 'is-published'}`}>
              {isDraft ? 'Draft' : 'Published'}
            </span>
          )}
        </div>
        {!!detail && <div className="schedule-drawer-header-subtitle">{detail}</div>}
      </div>
    </div>
  );
}

ScheduleDrawerHeader.propTypes = {
  isNew: PropTypes.bool,
  isDraft: PropTypes.bool,
  isEvent: PropTypes.bool,
  title: PropTypes.string,
  color: PropTypes.string,
  projectId: PropTypes.string,
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  timezone: PropTypes.string,
  showGantt: PropTypes.bool,
  ganttTaskName: PropTypes.string,
  onGanttClick: PropTypes.func,
};

ScheduleDrawerHeader.defaultProps = {
  isNew: false,
  isDraft: false,
  isEvent: false,
  title: '',
  color: undefined,
  projectId: undefined,
  startTime: undefined,
  endTime: undefined,
  timezone: undefined,
  showGantt: false,
  ganttTaskName: '',
  onGanttClick: undefined,
};
